import React, { useMemo } from "react";
import { Hyperlink, type CellContext, type Column } from "@racoon/ui";
import { Namespace, TranslationKey, useT, useDateFnsLocale } from "@racoon/i18n";
import { OrderRow } from "./adapters";
import { getAuthorName, getPriorityScore, STATUS_ORDER } from "./orders.helpers";
import { JobStatus, ConnectProto } from "@racoon/api";
import { PhotoReportModal } from "~/features/photo-report-modal/ui/photo-report.modal";
import { WhatsAppReportModal } from "~/features/whatsapp-report-modal/ui/whatsapp-report.modal";
import { OrderAuthor } from "../components/order-author";
import { JobIncident } from "../components/job-map/job-incident";
import { jobIncidentRank } from "~/shared/lib/job-incident";
import { badge } from "~/shared/helpers";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { IcAiOn, IcAiOff } from "@racoon/icons";
import { fromTimestamp } from "@racoon/lib";
import { format } from "date-fns";
import {
  formatVolume,
  getJobFactContainerCount,
  getJobFactVolume,
  getJobTariff,
} from "~/shared/lib/job-metrics";

interface UseOrderColumnsProps {
  isAiEnabled?: boolean;
  handleToggleAiVisibility?: (e: React.MouseEvent, r: OrderRow) => void;
  onRouteClick?: (routeId: number) => void;
  /** Clicking the job id opens the job for editing (same dialog as "Редактировать") */
  onOrderIdClick?: (row: OrderRow) => void;
  /** The job's counterparties, each opening its own card over the registry. */
  onCustomerClick?: (customer: ConnectProto.Organization) => void;
  onLocationClick?: (location: ConnectProto.PickupLocation) => void;
  onDriverClick?: (driver: ConnectProto.User) => void;
  /** Shows the truck on the dashboard map rather than opening a card. */
  onPlateClick?: (truckId: bigint) => void;
  /** A listed job's incidents to read it from; undefined until they arrive. */
  incidentsOf?: (job: ConnectProto.Job) => readonly ConnectProto.DriverIncident[] | undefined;
}

export const useOrderColumns = ({
  isAiEnabled = false,
  handleToggleAiVisibility,
  onRouteClick,
  onOrderIdClick,
  onCustomerClick,
  onLocationClick,
  onDriverClick,
  onPlateClick,
  incidentsOf,
}: UseOrderColumnsProps = {}) => {
  const t = useT();
  const dateFnsLocale = useDateFnsLocale();
  // Another organization's zone job (or one not yet known as ours) opens no customer or trip card
  const isOwnForWrite = useIsOwnOrganizationForWrite("job");

  const getSortDate = useMemo(() => {
    return (row: OrderRow): number => {
      const planStartAt = row.job?.plan_start_at;
      if (planStartAt) {
        const date =
          planStartAt instanceof Date
            ? planStartAt.getTime()
            : typeof planStartAt === "string"
              ? new Date(planStartAt).getTime()
              : 0;
        if (!isNaN(date) && date > 0) return date;
      }
      const createdAt = row.createdAt;
      if (createdAt) {
        const date = typeof createdAt === "string" ? new Date(createdAt).getTime() : 0;
        if (!isNaN(date) && date > 0) return date;
      }
      return 0;
    };
  }, []);

  const compareByPlanStartOrCreated = useMemo(() => {
    return (a: OrderRow, b: OrderRow): number => {
      const dateA = getSortDate(a);
      const dateB = getSortDate(b);
      return dateA - dateB;
    };
  }, [getSortDate]);

  const columns: Column<OrderRow>[] = useMemo(
    () => [
      {
        id: "number",
        header: t(Namespace.Orders, "columns.id").toUpperCase(),
        accessor: (r: OrderRow) => r.number,
        width: 70,
        sortable: true,
        cell: (info: CellContext<OrderRow, string | number>) => {
          const r = info.row.original as OrderRow;
          // Every job opens; a finished or cancelled one opens read-only.
          if (!onOrderIdClick) return info.getValue();
          return (
            <Hyperlink onOpen={() => onOrderIdClick(r)} data-testid="orders-job-link">
              {info.getValue()}
            </Hyperlink>
          );
        },
      },
      {
        id: "author",
        header: t(Namespace.Orders, "columns.author"),
        accessor: (r: OrderRow) => getAuthorName(r),
        width: 110,
        sortable: true,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, string>) => (
          <div className="flex w-full justify-center">
            <OrderAuthor row={info.row.original} />
          </div>
        ),
      },
      {
        id: "aiVisibility",
        header: t(Namespace.Orders, "columns.hide"),
        accessor: (r: OrderRow) => r.visible_to_ai,
        width: 80,
        sortable: true,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, boolean>) => {
          const r = info.row.original;
          return (
            <div
              className="flex items-center w-full cursor-pointer hover:opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleAiVisibility?.(e, r);
              }}
              title={
                r.visible_to_ai
                  ? t(Namespace.Orders, "ai.tooltipVisible")
                  : t(Namespace.Orders, "ai.tooltipHidden")
              }
            >
              <div className="mx-auto flex items-center">
                {r.visible_to_ai ? (
                  <IcAiOn className="w-6 h-6 text-brand-purple" />
                ) : (
                  <IcAiOff className="w-6 h-6 text-brand-red" />
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "statusText",
        header: t(Namespace.Orders, "columns.status"),
        accessor: (r: OrderRow) => r.status,
        cell: (info: CellContext<OrderRow, JobStatus>) => {
          const r = info.row.original;
          return (
            <div className="flex w-full">
              <div className="mx-auto">
                {badge(t(Namespace.Enum, r.status as TranslationKey), r.status as any)}
              </div>
            </div>
          );
        },
        width: 160,
        sortable: true,
        align: "center" as const,
        sortFn: (a: OrderRow, b: OrderRow) => {
          const aStatus = STATUS_ORDER[a.status as JobStatus] ?? 999;
          const bStatus = STATUS_ORDER[b.status as JobStatus] ?? 999;
          return aStatus - bStatus;
        },
      },
      {
        id: "incident",
        header: t(Namespace.Orders, "columns.incident"),
        width: 160,
        sortable: true,
        // Sorted on the server; the value also orders the review mode, sorted here.
        accessor: (r: OrderRow) => {
          const incidents = incidentsOf?.(r.rawJob);
          return incidents && jobIncidentRank(r.rawJob, incidents);
        },
        cell: (info: CellContext<OrderRow, unknown>) => (
          <JobIncident
            job={info.row.original.rawJob}
            incidents={incidentsOf?.(info.row.original.rawJob)}
          />
        ),
      },
      {
        id: "typeText",
        header: t(Namespace.Orders, "columns.type"),
        accessor: (r: OrderRow) => r.typeText,
        width: 220,
        sortable: true,
        cell: (info: CellContext<OrderRow, string>) => {
          return info.getValue();
        },
      },
      {
        id: "container_type",
        header: t(Namespace.Orders, "columns.container_type"),
        accessor: (r: OrderRow) => r.containertype?.name,
        width: 150,
        sortable: true,
        cell: (info: CellContext<OrderRow, string | undefined>) => {
          return info.getValue() ?? "—";
        },
      },
      {
        id: "planCount",
        header: t(Namespace.Orders, "columns.planCount"),
        // The job's own «Кол-во контейнеров»; jobs saved before the field have none
        accessor: (r: OrderRow) => r.rawJob.numberOfContainers || "-",
        width: 110,
        sortable: true,
        align: "center" as const,
      },
      {
        id: "factCount",
        header: t(Namespace.Orders, "columns.factCount"),
        accessor: (r: OrderRow) => getJobFactContainerCount(r.rawJob) ?? "-",
        width: 110,
        sortable: true,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, number | string>) => {
          return info.getValue();
        },
      },
      {
        id: "factVolume",
        header: t(Namespace.Orders, "columns.factVolume"),
        accessor: (r: OrderRow) => formatVolume(getJobFactVolume(r.rawJob)),
        width: 110,
        sortable: true,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, string>) => {
          return info.getValue();
        },
      },
      {
        id: "customer",
        header: t(Namespace.Orders, "columns.customer"),
        accessor: (r: OrderRow) => r.customer,
        width: 220,
        sortable: true,
        cell: (info: CellContext<OrderRow, string>) => {
          // The raw proto, not the adapted row: the card is fed the same
          // message the customers registry opens.
          const job = info.row.original.rawJob;
          const customer = job?.serviceReceiver;
          if (
            !onCustomerClick ||
            !customer ||
            isOwnForWrite(job?.serviceProviderId, job?.serviceReceiverId) !== true
          )
            return info.getValue();
          return <Hyperlink onOpen={() => onCustomerClick(customer)}>{info.getValue()}</Hyperlink>;
        },
      },
      {
        id: "objectName",
        header: t(Namespace.Orders, "columns.object"),
        accessor: (r: OrderRow) => r.objectName,
        width: 220,
        sortable: true,
        cell: (info: CellContext<OrderRow, string>) => {
          const location = info.row.original.rawJob?.pickupLocation;
          if (!onLocationClick || !location) return info.getValue();
          return <Hyperlink onOpen={() => onLocationClick(location)}>{info.getValue()}</Hyperlink>;
        },
      },
      // The responsible depot: a zone job may be another division's
      {
        id: "executor",
        header: t(Namespace.Orders, "columns.executor"),
        accessor: (r: OrderRow) => r.rawJob?.serviceProvider?.name ?? "—",
        width: 180,
      },
      {
        id: "priorityText",
        header: t(Namespace.Orders, "columns.priority"),
        accessor: (r: OrderRow) => r.priorityText,
        cell: (info: CellContext<OrderRow, string>) => {
          const priorityKey = info.getValue() as TranslationKey<Namespace.Enum>;
          const priorityValue = t(Namespace.Enum, priorityKey);
          const priorityLabel = t(Namespace.Table, "priority").toLocaleLowerCase();
          const priorityText = `${priorityValue}\u00A0${priorityLabel}`;
          return (
            <div className="flex w-full">
              <div className="mx-auto">{badge(priorityText, priorityKey)}</div>
            </div>
          );
        },
        width: 140,
        sortable: true,
        align: "center" as const,
        sortFn: (a: OrderRow, b: OrderRow) => {
          return getPriorityScore(a.priorityText) - getPriorityScore(b.priorityText);
        },
      },
      {
        id: "geozone",
        header: t(Namespace.Orders, "columns.geozone"),
        accessor: (r: OrderRow) => r.geozone,
        width: 100,
        sortable: true,
        cell: (info: CellContext<OrderRow, string | null>) => {
          return info.getValue() ?? "—";
        },
      },
      {
        id: "route",
        header: t(Namespace.Orders, "columns.route"),
        accessor: (r: OrderRow) => r.route,
        width: 110,
        sortable: true,
        cell: (info: CellContext<OrderRow, string | number | null>) => {
          const jobAny = info.row.original.job as any; // TODO(refactor): Remove 'as any' cast once the old REST endpoint is deprecated.
          const routeId = jobAny?.routeId || jobAny?.route_id;
          const value = info.getValue() ?? "—";
          const routeIdNum =
            typeof routeId === "bigint"
              ? Number(routeId)
              : typeof routeId === "string"
                ? parseInt(routeId, 10)
                : routeId;
          // Fetched as the active company's: another provider's trip would not load
          const job = info.row.original.rawJob;
          const isOwn = isOwnForWrite(job?.serviceProviderId, job?.serviceReceiverId) === true;
          if (routeIdNum && !isNaN(routeIdNum) && onRouteClick && isOwn) {
            return <Hyperlink onOpen={() => onRouteClick(routeIdNum)}>{value}</Hyperlink>;
          }
          return value;
        },
      },
      {
        id: "tw_start",
        header: t(Namespace.Orders, "columns.tw_start"),
        accessor: (r: OrderRow) => {
          const jobAny = r.job as any; // TODO(refactor): Remove 'as any' cast once the old REST endpoint is deprecated.
          return jobAny?.twStartAt || jobAny?.tw_start_at;
        },
        width: 180,
        sortable: true,
        sortFn: compareByPlanStartOrCreated,
        cell: (info: CellContext<OrderRow, Date | string>) => {
          const val = info.getValue() as any;
          if (!val) return "—";
          try {
            // Handle protobuf Timestamp
            if (val.seconds !== undefined) {
              const date = fromTimestamp(val);
              return date ? format(date, "HH:mm dd MMM yyyy", { locale: dateFnsLocale }) : "—";
            }
            return format(new Date(val), "HH:mm dd MMM yyyy", { locale: dateFnsLocale });
          } catch {
            return String(val);
          }
        },
      },
      {
        id: "createdAt",
        header: t(Namespace.Orders, "columns.createdAt"),
        accessor: (r: OrderRow) => r.createdAt,
        width: 180,
        sortable: true,
        cell: (info: CellContext<OrderRow, Date | string>) => {
          const val = info.getValue() as string;
          if (!val) return "—";
          try {
            return format(new Date(val), "HH:mm dd MMM yyyy", { locale: dateFnsLocale });
          } catch {
            return val;
          }
        },
      },
      {
        id: "finishedAt",
        header: t(Namespace.Orders, "columns.finishedAt"),
        accessor: (r: OrderRow) => r.finishedAt,
        width: 180,
        sortable: true,
        cell: (info: CellContext<OrderRow, Date | string | null>) => {
          const val = info.getValue() as string;
          if (!val) return "—";
          try {
            return format(new Date(val), "HH:mm dd MMM yyyy", { locale: dateFnsLocale });
          } catch {
            return val;
          }
        },
      },
      {
        id: "hasPhotoReport",
        header: t(Namespace.Orders, "columns.photo"),
        accessor: (r: OrderRow) => r.rawJob,
        width: 90,
        sortable: true,
        align: "center" as const,
        sortFn: (a: OrderRow, b: OrderRow) => {
          const aHasPhoto = a.hasPhotoReport ? 1 : 0;
          const bHasPhoto = b.hasPhotoReport ? 1 : 0;
          return aHasPhoto - bHasPhoto;
        },
        cell: (info: CellContext<OrderRow, any>) => {
          return (
            <div className="flex w-full justify-center">
              <div className="flex items-center gap-2">
                <PhotoReportModal
                  job={info.getValue()}
                  getJobs={() => info.table.getRowModel().rows.map((row) => row.original.rawJob)}
                />
                <WhatsAppReportModal job={info.row.original.rawJob} />
              </div>
            </div>
          );
        },
      },
      {
        id: "tariff",
        header: t(Namespace.Orders, "columns.tariff"),
        accessor: (r: OrderRow) => r.tariff,
        width: 110,
        sortable: true,
        cell: (info: CellContext<OrderRow, string | number | null>) => {
          const tariff = getJobTariff(info.row.original.rawJob);
          return tariff ? `${tariff} ₸` : "—";
        },
      },
      {
        id: "plateNumber",
        header: t(Namespace.Table, "plateNumber"),
        accessor: (r: OrderRow) => {
          const truck = r.driver?.truck as any;
          return truck?.plateNumber || truck?.plate_number;
        },
        width: 110,
        sortable: true,
        cell: (info: CellContext<OrderRow, any>) => {
          const r = info.row.original;
          const plate = info.getValue();
          const truckId = r.rawJob?.driverOnShift?.truck?.id;
          if (!plate) return "—";
          if (!onPlateClick || truckId === undefined) return plate;
          return <Hyperlink onOpen={() => onPlateClick(truckId)}>{plate}</Hyperlink>;
        },
      },
      {
        id: "driverName",
        header: t(Namespace.Table, "driver"),
        accessor: (r: OrderRow) => r.driver?.driver,
        width: 110,
        sortable: true,
        cell: (info: CellContext<OrderRow, any>) => {
          const val = info.getValue() as any;

          if (!val) return "-";
          const lastName = val.lastName || val.last_name || "";
          const firstName = val.firstName || val.first_name || "";
          const name = `${lastName} ${firstName}`.trim() || "-";
          // The adapted row's driver carries resolved (string) enums, which the
          // employee card reads back as numbers — so the card gets the raw one.
          const driver = info.row.original.rawJob?.driverOnShift?.driver;
          if (!onDriverClick || !driver || name === "-") return name;
          return <Hyperlink onOpen={() => onDriverClick(driver)}>{name}</Hyperlink>;
        },
      },
      {
        id: "comment",
        header: t(Namespace.Table, "comment"),
        accessor: (r: OrderRow) => r.comment,
        width: 110,
        sortable: true,
        cell: (info: CellContext<OrderRow, string | null>) => {
          return info.getValue() ?? "—";
        },
      },
    ],
    [
      t,
      dateFnsLocale,
      isOwnForWrite,
      handleToggleAiVisibility,
      onRouteClick,
      onOrderIdClick,
      onCustomerClick,
      onLocationClick,
      onDriverClick,
      onPlateClick,
      incidentsOf,
    ],
  ).filter((col) => {
    if (col.id === "aiVisibility" && !isAiEnabled) return false;
    return true;
  });

  return { columns, compareByPlanStartOrCreated };
};
